import React, { useState, useEffect, useRef, useCallback } from 'react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { useMsal } from '@azure/msal-react';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from 'recharts';

// Permission check component
const AccessDenied = () => (
  <div style={{
    padding: '2rem',
    textAlign: 'center',
    backgroundColor: '#f8d7da',
    border: '1px solid #f5c6cb',
    borderRadius: '8px',
    color: '#721c24'
  }}>
    <h3 style={{ margin: '0 0 0.5rem 0' }}>🔒 Access Denied</h3>
    <p style={{ margin: 0 }}>You don't have permission to view project reports.</p>
  </div>
);

// Print styles injected into the page
const printStyles = `
  @media print {
    body * {
      visibility: hidden;
    }
    
    #client-report-printable,
    #client-report-printable * {
      visibility: visible;
    }
    
    #client-report-printable {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      padding: 20px;
      background: white;
    }
    
    .no-print {
      display: none !important;
    }
    
    @page {
      size: landscape;
      margin: 10mm;
    }
  }
`;

// Same S-curve generation as Budget Report - but without rounding to avoid zeros
const generateNormalDistribution = (startDate, finishDate, totalHours) => {
  if (!startDate || !finishDate || !totalHours || totalHours <= 0) return [];
  
  const start = new Date(startDate);
  const end = new Date(finishDate);
  const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
  
  if (daysDiff <= 0) return [];
  
  const weeks = Math.ceil(daysDiff / 7);
  const data = [];
  const mean = weeks / 2;
  const stdDev = weeks / 6;
  
  let rawValues = [];
  let totalRaw = 0;
  
  for (let i = 0; i < weeks; i++) {
    const x = i + 0.5;
    const exponent = -0.5 * Math.pow((x - mean) / stdDev, 2);
    const value = Math.exp(exponent);
    rawValues.push(value);
    totalRaw += value;
  }
  
  let cumulative = 0;
  const currentDate = new Date(start);
  
  for (let i = 0; i < weeks; i++) {
    // Don't round - keep decimal precision to avoid zeros
    const periodHours = (rawValues[i] / totalRaw) * totalHours;
    cumulative += periodHours;
    
    const weekStart = new Date(currentDate);
    currentDate.setDate(currentDate.getDate() + 7);
    
    data.push({
      date: weekStart.toISOString().split('T')[0],
      period: periodHours,
      cumulative: cumulative
    });
  }
  
  return data;
};

// EXACT copy of combineAllDistributions from BudgetReport.js
const combineBudgetDistributions = (disciplineData) => {
  // Find overall date range across all disciplines
  let minDate = null;
  let maxDate = null;
  
  Object.values(disciplineData).forEach(disc => {
    ['shop', 'field'].forEach(type => {
      if (disc[type]?.startDate && disc[type]?.finishDate) {
        const start = new Date(disc[type].startDate);
        const end = new Date(disc[type].finishDate);
        if (!minDate || start < minDate) minDate = start;
        if (!maxDate || end > maxDate) maxDate = end;
      }
    });
  });
  
  if (!minDate || !maxDate) return [];
  
  // Generate weekly periods for the entire range
  const daysDiff = Math.ceil((maxDate - minDate) / (1000 * 60 * 60 * 24)) + 1;
  const weeks = Math.ceil(daysDiff / 7);
  
  const combined = [];
  const currentDate = new Date(minDate);
  
  // Pre-generate all distributions
  const distributions = {};
  Object.entries(disciplineData).forEach(([discId, data]) => {
    ['shop', 'field'].forEach(type => {
      if (data[type]?.startDate && data[type]?.finishDate && data[type]?.hours) {
        distributions[`${discId}_${type}`] = {
          data: generateNormalDistribution(data[type].startDate, data[type].finishDate, parseFloat(data[type].hours)),
          startDate: new Date(data[type].startDate),
          finishDate: new Date(data[type].finishDate)
        };
      }
    });
  });
  
  for (let i = 0; i < weeks; i++) {
    const weekStart = new Date(currentDate);
    const weekEnd = new Date(currentDate);
    weekEnd.setDate(weekEnd.getDate() + 6);
    currentDate.setDate(currentDate.getDate() + 7);
    
    let totalPeriod = 0;
    
    // For each distribution, find which week index this global week corresponds to
    Object.entries(distributions).forEach(([key, dist]) => {
      if (dist.data.length > 0) {
        // Calculate the week index relative to this distribution's start
        const distWeekIndex = Math.floor((weekStart - dist.startDate) / (7 * 24 * 60 * 60 * 1000));
        
        if (distWeekIndex >= 0 && distWeekIndex < dist.data.length) {
          totalPeriod += dist.data[distWeekIndex].period;
        }
      }
    });
    
    combined.push({
      date: weekStart.toISOString().split('T')[0],
      budget_period: totalPeriod
    });
  }
  
  // Calculate running cumulative totals
  let runningTotal = 0;
  combined.forEach(week => {
    runningTotal += week.budget_period;
    week.budget_cumulative = runningTotal;
  });
  
  return combined;
};

const ClientReport = ({ jobNumber }) => {
  const { instance, accounts } = useMsal();
  
  // Permission states
  const [hasPermission, setHasPermission] = useState(null);
  const [permissionLoading, setPermissionLoading] = useState(true);
  
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEmailPrompt, setShowEmailPrompt] = useState(false);
  const chartContainerRef = useRef(null);
  
  // Phase selection: 'estimation' (Phase 1) or 'execution' (Phase 2)
  const [selectedPhase, setSelectedPhase] = useState('estimation');
  const [phase2CurveData, setPhase2CurveData] = useState([]);
  
  // Store raw budget total for accurate progress calculation
  const [rawBudgetTotal, setRawBudgetTotal] = useState(0);

  // Check permissions on mount
  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const response = await authenticatedApiCall(instance, accounts, 'GET', '/auth/permissions-info');
        if (response.data.success) {
          const userPermissions = response.data.data.permissions || [];
          const isAdmin = response.data.data.isAdmin || false;
          setHasPermission(isAdmin || userPermissions.includes('reports.project'));
        } else {
          setHasPermission(false);
        }
      } catch (error) {
        console.error('Error checking permissions:', error);
        setHasPermission(false);
      } finally {
        setPermissionLoading(false);
      }
    };
    checkPermissions();
  }, [instance, accounts]);

  // Inject print styles
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.id = 'client-report-print-styles';
    styleElement.textContent = printStyles;
    document.head.appendChild(styleElement);
    
    return () => {
      const existingStyle = document.getElementById('client-report-print-styles');
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, []);

  const fetchReportData = useCallback(async () => {
    if (!jobNumber) return;
    
    try {
      setLoading(true);
      
      // Fetch EVM data, Budget baseline, and Schedule baseline (Phase 2)
      const [evmResponse, budgetResponse, scheduleResponse] = await Promise.all([
        authenticatedApiCall(instance, accounts, 'GET', `/evm-report?job_number=${jobNumber}`),
        authenticatedApiCall(instance, accounts, 'GET', `/budget-baseline/${jobNumber}`),
        authenticatedApiCall(instance, accounts, 'GET', `/schedule-baseline/${jobNumber}/curve`)
      ]);
      
      // Get earned data from EVM report
      const evmData = evmResponse.data.success ? (evmResponse.data.data || []) : [];
      
      // Store Phase 2 curve data
      if (scheduleResponse.data.success && scheduleResponse.data.data) {
        setPhase2CurveData(scheduleResponse.data.data);
      }
      
      // Calculate raw budget total (same as EVM Report for consistency)
      let calculatedBudgetTotal = 0;
      if (budgetResponse.data.success && budgetResponse.data.data) {
        Object.values(budgetResponse.data.data).forEach(disc => {
          ['shop', 'field'].forEach(type => {
            if (disc[type]?.hours) {
              calculatedBudgetTotal += parseFloat(disc[type].hours);
            }
          });
        });
      }
      setRawBudgetTotal(calculatedBudgetTotal);
      
      // Generate budget curve based on selected phase
      let budgetCurve = [];
      if (selectedPhase === 'execution' && scheduleResponse.data.success && scheduleResponse.data.data?.length > 0) {
        // Use Phase 2 (Execution) data from schedule_baseline_1
        budgetCurve = scheduleResponse.data.data;
      } else if (budgetResponse.data.success && budgetResponse.data.data) {
        // Use Phase 1 (Estimation) data
        budgetCurve = combineBudgetDistributions(budgetResponse.data.data);
      }
      
      // Merge the data - use budget curve as the base timeline
      // For each budget week, find earned data within the same week (±3 days)
      const findClosestEarned = (budgetDate, evmData) => {
        const budgetTime = new Date(budgetDate).getTime();
        const threeDays = 3 * 24 * 60 * 60 * 1000;
        
        for (const item of evmData) {
          const itemTime = new Date(item.date).getTime();
          if (Math.abs(itemTime - budgetTime) <= threeDays) {
            return {
              earned_period: item.earned_period || 0,
              earned_cumulative: item.earned_cumulative || 0
            };
          }
        }
        return null;
      };
      
      // Track which earned data points we've used
      let lastEarnedCumulative = 0;
      
      // For each budget week, find the closest/matching earned data
      const mergedData = budgetCurve.map(budgetItem => {
        const earnedData = findClosestEarned(budgetItem.date, evmData);
        
        if (earnedData) {
          lastEarnedCumulative = earnedData.earned_cumulative;
        }
        
        return {
          date: budgetItem.date,
          budget_period: budgetItem.budget_period,
          budget_cumulative: budgetItem.budget_cumulative,
          earned_period: earnedData?.earned_period || 0,
          earned_cumulative: earnedData ? earnedData.earned_cumulative : lastEarnedCumulative
        };
      });
      
      // Also add any earned dates that come AFTER the budget curve ends
      const lastBudgetDate = budgetCurve.length > 0 ? new Date(budgetCurve[budgetCurve.length - 1].date) : null;
      const lastBudgetCumulative = budgetCurve.length > 0 ? budgetCurve[budgetCurve.length - 1].budget_cumulative : 0;
      
      evmData.forEach(item => {
        const itemDate = new Date(item.date);
        if (lastBudgetDate && itemDate > lastBudgetDate) {
          mergedData.push({
            date: item.date,
            budget_period: 0,
            budget_cumulative: lastBudgetCumulative,
            earned_period: item.earned_period || 0,
            earned_cumulative: item.earned_cumulative || 0
          });
        }
      });
      
      // Sort by date
      mergedData.sort((a, b) => new Date(a.date) - new Date(b.date));
      
      console.log('Final merged data:', mergedData.length, 'points');
      console.log('Merged data sample (first 3):', mergedData.slice(0, 3));
      
      // Check for budget zeros in merged data
      const budgetZeros = mergedData.filter(w => w.budget_period === 0).length;
      console.log('Merged data - weeks with zero budget_period:', budgetZeros);
      
      setChartData(mergedData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching Client report:', error);
      setLoading(false);
    }
  }, [instance, accounts, jobNumber, selectedPhase]);

  useEffect(() => {
    if (jobNumber) {
      fetchReportData();
    }
  }, [jobNumber, fetchReportData, selectedPhase]);

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '0';
    return parseFloat(num).toLocaleString('en-US', { maximumFractionDigits: 0 });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleSendToClient = () => {
    window.print();
    setTimeout(() => {
      setShowEmailPrompt(true);
    }, 500);
  };

  const openOutlookEmail = () => {
    const reportDate = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    
    const subject = encodeURIComponent(`Client Progress Report - Job ${jobNumber}`);
    const body = encodeURIComponent(
      'Dear Client,\n\n' +
      `Please find attached the latest progress report for Job ${jobNumber}.\n\n` +
      'Report Date: ' + reportDate + '\n\n' +
      'This report includes:\n' +
      '• Earned hours vs Budget hours over time\n' +
      '• Period-based and cumulative progress metrics\n\n' +
      'Please review and let us know if you have any questions.\n\n' +
      'Best regards'
    );
    
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
    setShowEmailPrompt(false);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          background: 'white',
          padding: '12px',
          border: '1px solid #d1d5db',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <p style={{ fontWeight: '600', marginBottom: '8px', color: '#1f2937' }}>
            {formatDate(label)}
          </p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color, margin: '4px 0', fontSize: '0.875rem' }}>
              {entry.name}: {formatNumber(entry.value)} hrs
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const reportDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const generatedDate = new Date().toLocaleString('en-US', { 
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  // Calculate summary values
  // Use rawBudgetTotal for accurate progress % (same as EVM Report)
  // Fall back to chart cumulative if rawBudgetTotal not available
  const totalBudget = rawBudgetTotal > 0 ? rawBudgetTotal : (chartData.length > 0 ? Math.max(...chartData.map(d => d.budget_cumulative || 0)) : 0);
  const totalEarned = chartData.length > 0 ? Math.max(...chartData.map(d => d.earned_cumulative || 0)) : 0;

  // Permission check - show loading or access denied
  if (permissionLoading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p style={{ color: '#6b7280' }}>Checking permissions...</p>
      </div>
    );
  }

  if (!hasPermission) {
    return <AccessDenied />;
  }

  if (!jobNumber) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2 style={{ color: '#6b7280' }}>No project selected</h2>
        <p style={{ color: '#9ca3af' }}>Please select a project from the dashboard to view the Client Report.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '1rem', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header with button - hidden in print */}
      <div className="no-print" style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ fontSize: '2rem', fontWeight: '700', color: '#1f2937', marginBottom: '0.5rem' }}>
            📋 Client Report
          </h1>
          <p style={{ color: '#6b7280', fontSize: '0.95rem' }}>
            Report for client: Earned hours vs budget hours over time
          </p>
        </div>
        <button
          onClick={handleSendToClient}
          disabled={loading || chartData.length === 0}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.75rem 1.5rem',
            backgroundColor: loading || chartData.length === 0 ? '#9ca3af' : '#2563eb',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '0.95rem',
            fontWeight: '600',
            cursor: loading || chartData.length === 0 ? 'not-allowed' : 'pointer',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            if (!loading && chartData.length > 0) {
              e.target.style.backgroundColor = '#1d4ed8';
              e.target.style.transform = 'translateY(-1px)';
            }
          }}
          onMouseLeave={(e) => {
            if (!loading && chartData.length > 0) {
              e.target.style.backgroundColor = '#2563eb';
              e.target.style.transform = 'translateY(0)';
            }
          }}
        >
          📧 Send to Client
        </button>
      </div>

      {/* Summary Cards - visible on screen, simplified for print */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{
          background: 'white',
          padding: '1rem',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          border: '1px solid rgba(0,0,0,0.06)'
        }}>
          <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.25rem' }}>Total Budget</div>
          <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#8b5cf6' }}>
            {formatNumber(totalBudget)}
          </div>
        </div>
        <div style={{
          background: 'white',
          padding: '1rem',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          border: '1px solid rgba(0,0,0,0.06)'
        }}>
          <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.25rem' }}>Total Earned</div>
          <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#3b82f6' }}>
            {formatNumber(totalEarned)}
          </div>
        </div>
        <div style={{
          background: 'white',
          padding: '1rem',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          border: '1px solid rgba(0,0,0,0.06)'
        }}>
          <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.25rem' }}>Progress</div>
          <div style={{ fontSize: '1.5rem', fontWeight: '700', color: totalEarned >= totalBudget ? '#10b981' : '#f59e0b' }}>
            {totalBudget > 0 ? ((totalEarned / totalBudget) * 100).toFixed(1) : 0}%
          </div>
        </div>
      </div>

      {/* Phase Toggle - No Print */}
      <div className="no-print" style={{
        display: 'flex',
        gap: '1rem',
        marginBottom: '1rem',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        <span style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
          📋 Budget Source:
        </span>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={() => setSelectedPhase('estimation')}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: selectedPhase === 'estimation' ? '#fef3c7' : '#f3f4f6',
              color: selectedPhase === 'estimation' ? '#92400e' : '#6b7280',
              border: selectedPhase === 'estimation' ? '2px solid #f59e0b' : '1px solid #d1d5db',
              borderRadius: '6px',
              fontWeight: selectedPhase === 'estimation' ? '600' : '400',
              cursor: 'pointer',
              transition: 'all 0.15s'
            }}
          >
            Phase 1: Estimation
          </button>
          <button
            onClick={() => phase2CurveData.length > 0 && setSelectedPhase('execution')}
            disabled={phase2CurveData.length === 0}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: selectedPhase === 'execution' ? '#dbeafe' : '#f3f4f6',
              color: selectedPhase === 'execution' ? '#1e40af' : '#6b7280',
              border: selectedPhase === 'execution' ? '2px solid #3b82f6' : '1px solid #d1d5db',
              borderRadius: '6px',
              fontWeight: selectedPhase === 'execution' ? '600' : '400',
              cursor: phase2CurveData.length > 0 ? 'pointer' : 'not-allowed',
              opacity: phase2CurveData.length > 0 ? 1 : 0.5,
              transition: 'all 0.15s'
            }}
          >
            Phase 2: Execution {phase2CurveData.length === 0 && '(No data)'}
          </button>
        </div>
      </div>

      {/* Printable Report Content */}
      <div id="client-report-printable" ref={chartContainerRef}>
        {/* Print Header - only visible in print */}
        <div style={{ 
          borderBottom: '3px solid #2563eb', 
          paddingBottom: '15px', 
          marginBottom: '20px'
        }}>
          <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#1f2937', margin: '0 0 8px 0' }}>
            Client Progress Report
          </h1>
          <p style={{ fontSize: '14px', color: '#4b5563', margin: '2px 0' }}>
            <strong>Job Number:</strong> {jobNumber}
          </p>
          <p style={{ fontSize: '14px', color: '#4b5563', margin: '2px 0' }}>
            <strong>Report Date:</strong> {reportDate}
          </p>
        </div>

        {/* Overview Section */}
        <div style={{ marginBottom: '20px' }}>
          <h2 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', margin: '0 0 8px 0' }}>
            Project Progress Overview
          </h2>
          <p style={{ fontSize: '13px', color: '#4b5563', lineHeight: '1.6', margin: 0 }}>
            This report presents the earned hours versus budget hours over time for the project.
            The chart below illustrates both period-based and cumulative progress metrics.
          </p>
        </div>

        {/* Chart Container */}
        <div style={{
          background: '#fafafa',
          padding: '20px',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
          marginBottom: '20px'
        }}>
          <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', margin: '0 0 15px 0' }}>
            Hours Over Time
          </h3>
          
          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
              Loading...
            </div>
          ) : chartData.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
              No data available for this job
            </div>
          ) : (
            <div style={{ width: '100%', height: '400px' }}>
              <ComposedChart
                width={1100}
                height={380}
                data={chartData}
                margin={{ top: 20, right: 50, left: 40, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={formatDate}
                  style={{ fontSize: '12px' }}
                  stroke="#6b7280"
                />
                <YAxis 
                  yAxisId="left"
                  domain={[0, 'auto']}
                  label={{ 
                    value: 'Period Hours', 
                    angle: -90, 
                    position: 'insideLeft',
                    dx: -10,
                    style: { fontSize: '12px', fill: '#6b7280', textAnchor: 'middle' }
                  }}
                  style={{ fontSize: '12px' }}
                  stroke="#6b7280"
                />
                <YAxis 
                  yAxisId="right"
                  orientation="right"
                  label={{ 
                    value: 'Cumulative Hours', 
                    angle: 90, 
                    position: 'insideRight',
                    dx: 10,
                    style: { fontSize: '12px', fill: '#6b7280', textAnchor: 'middle' }
                  }}
                  style={{ fontSize: '12px' }}
                  stroke="#6b7280"
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend 
                  wrapperStyle={{ fontSize: '12px' }}
                  iconType="square"
                />
                
                <Bar 
                  yAxisId="left"
                  dataKey="earned_period" 
                  fill="#3b82f6" 
                  name="Earned Period (hrs)"
                  radius={[4, 4, 0, 0]}
                />
                <Bar 
                  yAxisId="left"
                  dataKey="budget_period" 
                  fill="#8b5cf6" 
                  name="Budget Period (hrs)"
                  radius={[4, 4, 0, 0]}
                />
                
                <Line 
                  yAxisId="right"
                  type="monotone" 
                  dataKey="earned_cumulative" 
                  stroke="#2563eb" 
                  strokeWidth={3}
                  name="Earned Cumulative (hrs)"
                  dot={{ r: 3, fill: '#2563eb' }}
                  activeDot={{ r: 5 }}
                />
                <Line 
                  yAxisId="right"
                  type="monotone" 
                  dataKey="budget_cumulative" 
                  stroke="#9333ea" 
                  strokeWidth={3}
                  name="Budget Cumulative (hrs)"
                  dot={{ r: 3, fill: '#9333ea' }}
                  activeDot={{ r: 5 }}
                />
              </ComposedChart>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ 
          borderTop: '1px solid #e5e7eb', 
          paddingTop: '15px', 
          marginTop: '20px',
          textAlign: 'center'
        }}>
          <p style={{ fontSize: '11px', color: '#9ca3af', margin: 0 }}>
            Generated on {generatedDate} | LGL Tools Progress Tracking System
          </p>
        </div>
      </div>

      {/* Email Prompt Modal */}
      {showEmailPrompt && (
        <div 
          className="no-print"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
        >
          <div style={{
            background: 'white',
            padding: '2rem',
            borderRadius: '12px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>📧</div>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#1f2937', margin: 0 }}>
                Ready to Send Email
              </h2>
            </div>
            
            <div style={{ 
              background: '#f0fdf4', 
              border: '1px solid #bbf7d0', 
              borderRadius: '8px', 
              padding: '1rem',
              marginBottom: '1.5rem'
            }}>
              <p style={{ fontSize: '0.9rem', color: '#166534', margin: '0 0 0.75rem 0', fontWeight: '600' }}>
                ✓ PDF saved successfully!
              </p>
              <p style={{ fontSize: '0.85rem', color: '#15803d', margin: 0 }}>
                Click the button below to open Outlook, then <strong>attach the PDF</strong> you just saved.
              </p>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={() => setShowEmailPrompt(false)}
                style={{
                  flex: 1,
                  padding: '0.75rem 1rem',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={openOutlookEmail}
                style={{
                  flex: 2,
                  padding: '0.75rem 1rem',
                  background: '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                📧 Open Outlook
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientReport;
